import { Text } from "@chakra-ui/layout";
import { t } from "@lingui/macro";
import { DataTable } from "@src/components/ui-kit";
import { currency, HUNDRED_PERCENT, percent } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import type { Store } from "./Store";
import type { SummaryProps } from "./Summary";

type Props = Omit<SummaryProps, "summary"> & {
  summary: Store["finishedProjectsSummary"];
};

const FinishedSummary: FC<Props> = ({ summary, projectsCount, isLoaded }) => {
  const {
    profit,
    net_profit,
    gross_income,
    net_margin_percent,
    our_work_efficiency,
    credit,
  } = summary ?? {
    profit: 0,
    net_profit: 0,
    gross_income: 0,
    net_margin_percent: 0,
    our_work_efficiency: 0,
    credit: 0,
  };

  return (
    <DataTable.SummaryBox justify="space-between">
      <DataTable.SummaryItem label={t`Projects`} isLoaded={isLoaded}>
        {projectsCount}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Budget Profit`}
        isLoaded={isLoaded}
        labelTooltip={t`How much the project is running over or under its Client Work Budget, plus the profit made on Production. Formula: (Client Work Budget − Work Spent) + Profit (Production, Media, Royalties)`}
      >
        {currency.formatByWorkspace(profit, {
          czkNoFractionDigits: true,
        })}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Revenue`}
        isLoaded={isLoaded}
        labelTooltip={t`The total amount invoiced to the client — combined across Work, Production, Media and Royalties.`}
      >
        {currency.formatByWorkspace(credit, {
          czkNoFractionDigits: true,
        })}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Net Profit`}
        isLoaded={isLoaded}
        labelTooltip={t`Real profit after all costs, including the labor cost. = Profit (Work + Production + Media + Royalties)`}
      >
        {currency.formatByWorkspace(net_profit, {
          czkNoFractionDigits: true,
        })}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Net Margin`}
        isLoaded={isLoaded}
        coloredValue={net_margin_percent ?? 0}
        labelTooltip={t`EBIT Margin — the share of revenue that turns into profit. = Net Profit ÷ Revenue × 100`}
      >
        {percent.format(net_margin_percent ?? 0)}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Work efficiency`}
        isLoaded={isLoaded}
        labelTooltip={t`Work Budget − Actual Cost of Work (tracked time × rate). Over 100% = spent less than planned; below 100% = spent more.`}
      >
        <Text
          color={
            our_work_efficiency < HUNDRED_PERCENT ? "red.500" : "green.500"
          }
          fontWeight="inherit"
          fontSize="inherit"
        >
          {percent.format(our_work_efficiency)}
        </Text>
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Gross income`}
        labelTooltip={t`It is a summary of Revenue (Work) + Profit (Production, Media) + Royalties.`}
        isLoaded={isLoaded}
      >
        {currency.formatByWorkspace(gross_income, {
          czkNoFractionDigits: true,
        })}
      </DataTable.SummaryItem>
    </DataTable.SummaryBox>
  );
};

export default observer(FinishedSummary);
